import * as yup from 'yup';
import { FieldError } from 'react-hook-form';
import { View } from 'react-native';
import React, { useMemo, useRef, useState } from 'react';

import { Button } from '@components/Button';
import { CircledIcon } from '@components/CircledIcon';
import { Input } from '@components/Input';
import { Typography } from '@components/Typography';
import { firebase } from '@lib/firebase';
import { logger } from '@lib/logger';
import { makeStyles } from '@providers/Theme';
import { trackEvent } from '@utils/analytics';
import { useLookupsStore } from '@stores/lookups';
import { useToast } from '@providers/Toast';
import { useTranslation } from '@providers/I18n';
import {
  ChangedItemSizesPrice,
  NewItemPrice,
  UnAvailableItem,
  UnAvailableItemRequest,
  UpdateOrderStatusData,
  updateOrderStatus,
} from '@lib/api/updateOrderStatus';
import {
  CheckboxGroup,
  CheckboxGroupItem,
  useCheckboxGroupValue,
} from '@components/CheckboxGroup';
import { Order, OrderMessageEntry } from '@lib/types';
import { RadioGroup, RadioGroupItem } from '@components/RadioGroup';
import { Wizard, WizardRef } from '@components/Wizard';

import { useUnavailabilityInterval } from './utils';

const useStyles = makeStyles((theme) => ({
  button: {
    marginRight: theme.spacing(2),
  },
  field: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  price: {
    flex: 5,
  },
  currency: {
    flex: 1,
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
  subtitle: {
    color: '#E2A21F',
    textAlign: 'center',
    marginBottom: 10,
  },
}));

type RejectOrderProps = {
  order: Order;
};

export type UnavailableItem = {
  value: string;
  label: string;
  error?: FieldError;
};

type GAData = {
  order_short_code: string;
  restaurant_name: string;
  branch_id: string;
  reject_reason: string;
  items?: string[];
};

type StepKey =
  | 'rejection-reason'
  | 'unavailable-item-selection'
  | 'item-wrong-price';

export const RejectOrder: React.FC<RejectOrderProps> = ({ order }) => {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const lookups = useLookupsStore();

  const toast = useToast();

  const [selectedReason, setSelectedReason] =
    useState<RadioGroupItem | null>(null);

  const [selectedUnavailableItems, setSelectedUnavailableItems] = useState<
    Record<string, UnavailableItem>
  >({});

  const [comment, setComment] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [GAData, setGAData] = useState<GAData | null>(null);
  const wizardRef = useRef<WizardRef<StepKey>>(null);

  const [
    unavailableOrderItems,
    toggleUnavailableOrderItem,
    resetUnavailableOrderItems,
  ] = useCheckboxGroupValue();

  const lang = i18n.language;
  const orderId = order?.orderMessage?.data?.orderShortCode;
  const toastId = `reject-order-toast-${orderId}`;

  const schema = yup
    .object()
    .shape({
      newPrice: yup.number().typeError('Price must be a number').min(0),
    })
    .required();

  const onChangePrice = async (item: OrderMessageEntry, newValue: string) => {
    try {
      await schema.validate({ newPrice: newValue });
      setHasError(false);
      setSelectedUnavailableItems((current) => ({
        ...current,
        [item.uuid]: {
          value: newValue,
          label: 'newPrice',
        },
      }));
    } catch (error) {
      logger.error('error', error);
      shouldHasError();
      setSelectedUnavailableItems((current) => ({
        ...current,
        [item.uuid]: {
          value: '',
          label: 'newPrice',
          error: {
            type: 'min',
            message: t(
              'orderView.rejectOrder.validation.newPrice',
              'Price must be number',
            ),
          },
        },
      }));
    }
  };

  const itemUnavailabilityInterval: RadioGroupItem[] =
    useUnavailabilityInterval();

  // order items
  const orderItems: CheckboxGroupItem[] = useMemo(() => {
    const items = order?.orderMessage?.data?.categories?.map((cat) => {
      return cat.items.map((item) => {
        return {
          value: item.uuid,
          details:
            selectedReason?.value === 'REJECTED_ITEMS_UNAVAILABLE' ? (
              <RadioGroup
                size={6}
                items={itemUnavailabilityInterval}
                onItemPress={(duration) => {
                  setSelectedUnavailableItems((current) => ({
                    ...current,
                    [item.uuid]: duration,
                  }));
                  setHasError(false);
                }}
                value={selectedUnavailableItems[item.uuid]}
              />
            ) : (
              <View style={styles.field}>
                <Input
                  onChange={(newValue) => onChangePrice(item, newValue)}
                  value={selectedUnavailableItems[item.uuid]?.value}
                  placeholder={'00.00'}
                  keyboardType="numeric"
                  error={selectedUnavailableItems[item.uuid]?.error}
                  style={{ root: styles.price }}
                />
                <Typography
                  weight="regular"
                  size={{ xs: 'sm', lg: 'md' }}
                  style={styles.currency}>
                  {t('common.egp')}
                </Typography>
              </View>
            ),
          label:
            lang === 'ar'
              ? cat.arName + ': ' + item.item.arName
              : cat.enName + ': ' + item.item.enName,
        };
      });
    });

    return items?.flatMap(item => item) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lang,
    selectedReason?.value,
    selectedUnavailableItems,
    unavailableOrderItems,
  ]);

  // rejection reasons
  const rejectionReasons: RadioGroupItem[] = useMemo(() => {
    if (lookups.value?.rejectionReasons) {
      const reasons = order?.orderMessage?.data?.fleet
        ? lookups.value?.rejectionReasons.filter(
            (reason) =>
              reason.value !== 'REJECTED_ADDRESS_NOT_CLEAR' &&
              reason.value !== 'REJECTED_ADDRESS_OUT_OF_COVERAGE',
          )
        : lookups.value?.rejectionReasons;

      return reasons?.map((reason) => {
        return {
          value: reason.value,
          label: lang === 'ar' ? reason.titleAr : reason.titleEn,
        };
      });
    }
    return [];
  }, [lang, lookups.value?.rejectionReasons, order?.orderMessage?.data?.fleet]);

  // close reject order modal
  const handleCloseModal = () => {
    // reset all values
    setIsOpen(false);
    setComment('');
    setSelectedReason(null);
    setSelectedUnavailableItems({});
    resetUnavailableOrderItems();
  };

  const handleRejectionReasonChange = async () => {
    if (selectedReason) {
      const gaObj = {
        order_short_code: order?.orderMessage?.data?.orderShortCode,
        restaurant_name: order?.orderMessage?.ref?.restaurant?.name?.enName,
        branch_id: order?.orderMessage?.ref?.branch?.uuid,
        reject_reason: selectedReason.value,
      };
      setGAData(gaObj);

      if (
        selectedReason?.value === 'REJECTED_ITEMS_UNAVAILABLE' ||
        selectedReason?.value === 'REJECTED_WRONG_PRICES'
      ) {
        wizardRef.current?.goTo('unavailable-item-selection');
      } else {
        await rejectOrder({
          ordersUUIDs: [order?.orderMessage?.uuid],
          comment,
          actionType: selectedReason?.value,
          requestType: 'SET_ORDER_AS_REJECTED_FOR_OTHER_REASONS',
          time: Date.now(),
        });

        GAData && trackEvent('Reject Order', GAData);
      }
    }
  };

  const onUnavailableItemsSelected = async () => {
    if (selectedReason?.value === 'REJECTED_ITEMS_UNAVAILABLE') {
      const unavailableItemRequests: UnAvailableItemRequest[] =
        unavailableOrderItems.map((item) => ({
          orderItemUUID: item.value,
          unavailabilityInterval: selectedUnavailableItems[item.value]?.value,
        }));
      const unAvailableItems: UnAvailableItem[] = [
        {
          orderUUID: order?.orderMessage?.uuid,
          unavailableItemRequests,
        },
      ];
      const itemNames = unavailableOrderItems.map((item) => item.label);
      GAData && (GAData.items = itemNames);

      await rejectOrder({
        unAvailableItems,
        comment,
        actionType: selectedReason?.value,
        requestType: 'SET_ORDER_AS_REJECTED_FOR_ITEM_NOT_AVAILABLE',
        time: Date.now(),
      });

      // GA4 event for Item Availability
      unavailableOrderItems.map((item) => {
        trackEvent('Item Availability', {
          order_short_code: order?.orderMessage?.data?.orderShortCode,
          restaurant_name: order?.orderMessage?.ref?.restaurant?.name?.enName,
          branch_id: order?.orderMessage?.ref?.branch?.uuid,
          source: 'Order Details',
          item_name: item.label,
          action: 'unavailable',
          duration: selectedUnavailableItems[item.value].value,
        });
      });
    } else {
      const filteredItems = unavailableOrderItems.filter(
        (item) => !selectedUnavailableItems[item.value]?.error,
      );

      const newItemPrice: NewItemPrice[] = filteredItems.map((item) => ({
        orderItemUUID: item.value,
        newPrice: Number(selectedUnavailableItems[item.value]?.value),
      }));

      const changedItemSizesPrices: ChangedItemSizesPrice[] = [
        {
          orderUUID: order?.orderMessage?.uuid,
          newItemPrice,
        },
      ];

      await rejectOrder({
        changedItemSizesPrices,
        comment,
        actionType: selectedReason?.value,
        requestType: 'SET_ORDER_AS_REJECTED_FOR_PRICE_CHANGE',
        time: Date.now(),
      });
    }
    GAData  && trackEvent('Reject Order', GAData);
  };

  const rejectOrder = async (data: UpdateOrderStatusData) => {
    try {
      handleCloseModal();
      toast.show(
        toastId,
        'loading',
        <Typography>
          {t('orderView.rejectModal.toast.loading.title', 'Reject order')}
        </Typography>,
        <Typography>
          {t('orderView.rejectModal.toast.loading.message', 'Rejecting order')}{' '}
          <Typography weight="bold">#{orderId}</Typography> ...
        </Typography>,
      );
      setIsSubmitting(true);

      await updateOrderStatus(data);
      toast.show(
        toastId,
        'success',
        <Typography>
          {t('orderView.rejectModal.toast.success.title', 'Order rejected')}
        </Typography>,
        <Typography>
          {t(
            'orderView.rejectModal.toast.success.message',
            'Order rejected successfully',
          )}
        </Typography>,
      );
    } catch (error) {
      logger.error('error', error);
      toast.show(
        toastId,
        'error',
        <Typography>
          {t('orderView.common.toast.error.title', 'Something went wrong!')}
        </Typography>,
        <Typography>
          {t(
            'orderView.common.toast.error.message',
            'Something went wrong while rejecting order. Please try again later.',
          )}
        </Typography>,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onBackButtonPress = () => {
    resetUnavailableOrderItems();
    setSelectedUnavailableItems({});
  };

  const shouldHasError = () => {
    const filteredItems = Object.keys(selectedUnavailableItems).filter(
      (itemId) => !selectedUnavailableItems[itemId].error,
    );
    if (filteredItems.length == 0) {
      setHasError(true);
    }
  };
  const supportPhoneNumber = firebase.getRemoteConfigString('SUPPORT_NUMBER');
  return (
    <>
      <Button
        variant="secondary"
        style={styles.button}
        onPress={() => setIsOpen(true)}>
        {t('orderView.rejectButton', 'Reject')}
      </Button>
      <Wizard
        ref_={wizardRef}
        open={isOpen}
        onRequestClose={function (): void {
          handleCloseModal();
        }}
        onBackButtonPress={onBackButtonPress}
        icon={<CircledIcon icon="close" preset="error" />}
        steps={[
          {
            key: 'rejection-reason',
            title: t(
              'orderView.rejectOrder.step1.title',
              'Why do you want to reject this order?',
            ),
            subtitle: t(
              'orderView.rejectOrder.step1.subTitle',
              'Call us on 02 25992670 in case you have a problem with the order cart.',
            ),
            content: (
              <View>
                <Typography
                  weight="bold"
                  size={{ xs: 'md', lg: 'xl' }}
                  style={styles.subtitle}>
                  {t(
                    'orderView.rejectOrder.step1.callUsNotTheCustomer',
                    'Don’t call the customer!',
                    {
                      phoneNumber: supportPhoneNumber,
                    },
                  )}
                </Typography>
                <RadioGroup
                  items={rejectionReasons}
                  onItemPress={setSelectedReason}
                  value={selectedReason}
                />
                <Input
                  onChange={setComment}
                  value={comment}
                  label={t('orderView.rejectOrder.step1.comment', 'Comment')}
                  placeholder={t(
                    'orderView.rejectOrder.step1.commentPlaceholder',
                    'Enter your comment..',
                  )}
                  numberOfLines={3}
                />
              </View>
            ),
            actions: [
              <Button
                variant="primary"
                disabled={!selectedReason?.value}
                loading={isSubmitting}
                onPress={() => {
                  handleRejectionReasonChange();
                }}>
                {t(
                  'orderView.rejectOrder.step1.confirmBtn',
                  'Confirm rejection reason',
                )}
              </Button>,
              <Button
                variant="secondary"
                disabled={isSubmitting}
                onPress={() => handleCloseModal()}>
                {t('orderView.rejectOrder.cancelBtn', 'Cancel')}
              </Button>,
            ],
          },
          {
            key: 'unavailable-item-selection',
            title:
              selectedReason?.value === 'REJECTED_WRONG_PRICES'
                ? t('orderView.rejectOrder.step3.title', 'Wrong Prices')
                : t(
                    'orderView.rejectOrder.step2.title',
                    'Item(s) not available',
                  ),
            subtitle:
              selectedReason?.value === 'REJECTED_WRONG_PRICES'
                ? t(
                    'orderView.rejectOrder.step3.subTitle',
                    'Choose the items and add their correct prices.',
                  )
                : t(
                    'orderView.rejectOrder.step2.subTitle',
                    'Choose the unavailable item and the unavailable duration.',
                  ),
            content: (
              <View>
                <CheckboxGroup
                  items={orderItems}
                  onItemPress={(item) => {
                    toggleUnavailableOrderItem(item);
                    const current = selectedUnavailableItems;
                    delete current[item.value];
                    shouldHasError();
                    setSelectedUnavailableItems(current);
                  }}
                  value={unavailableOrderItems}
                />
              </View>
            ),
            actions: [
              <Button
                variant="primary"
                disabled={
                  !Object.keys(selectedUnavailableItems).length || hasError
                }
                loading={isSubmitting}
                onPress={onUnavailableItemsSelected}>
                {t(
                  'orderView.rejectOrder.step2.confirmBtn',
                  'Confirm Item(s) not avaiable',
                )}
              </Button>,
              <Button
                variant="secondary"
                disabled={isSubmitting}
                onPress={() => handleCloseModal()}>
                {t('orderView.rejectOrder.cancelBtn', 'Cancel')}
              </Button>,
            ],
          },
        ]}
      />
    </>
  );
};
